'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroCarousel() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    // Trigger animations after hydration
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (!isHydrated) {
    return (
      <div className="relative h-screen bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="absolute inset-0 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="relative h-screen overflow-hidden bg-background">
      {/* Background Image with Parallax Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`w-full h-full transition-transform duration-[2000ms] ease-out ${
          isVisible ? 'scale-100' : 'scale-110'
        }`}>
          <AppImage
            src="/assets/images/rocket_gen_img_1556a8d96-1764540197249-1765635682676.png"
            alt="Modern medical operating room featuring C-arm imaging equipment, surgical table, and professional medical environment"
            className="w-full h-full object-cover"
            priority
          />
        </div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 h-full flex items-center justify-center px-4">
        <div className="text-center max-w-4xl mx-auto">
          {/* Company Name with Fade-in and Slide-up Animation */}
          <h1 
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 md:mb-6 transition-all duration-1000 ease-out whitespace-nowrap ${
              isVisible 
                ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-8'
            }`}
            style={{
              fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
              letterSpacing: '-0.02em',
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)',
              color: '#f8f9fa'
            }}
          >
            AIVA Medical Supply
          </h1>

          {/* Subtitle with Delayed Fade-in and Slide-up Animation */}
          <p 
            className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light transition-all duration-1000 ease-out delay-300 ${
              isVisible 
                ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-8'
            }`}
            style={{
              fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
              letterSpacing: '0.02em',
              textShadow: '0 3px 8px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.2)',
              color: '#e8eaed'
            }}
          >
            Medical & Surgical Equipment
          </p>

          {/* Subtle Decorative Line */}
          <div 
            className={`mt-8 md:mt-10 mx-auto w-24 h-0.5 bg-gradient-to-r from-transparent via-white/60 to-transparent transition-all duration-1000 ease-out delay-500 ${
              isVisible 
                ? 'opacity-100 scale-x-100' :'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>

      {/* Subtle Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <div 
          className={`transition-all duration-1000 ease-out delay-700 ${
            isVisible 
              ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-4'
          }`}
        >
          <div className="animate-bounce">
            <svg 
              className="w-6 h-6 text-white/70" 
              fill="none" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}